// -----------------------------------------------------------------------------
// ui/panels/media.ts — import media + the media library.
//
// Click or drop to import video/images. Files are probed (via the engine's media
// manager) into assets, persisted to scratch storage, and listed here. Clicking a
// library item appends it to the timeline. Everything is local — the hint makes
// the "nothing is uploaded" promise explicit. (CLAUDE.md §6 Tier 0: media-panel
// import.)
// -----------------------------------------------------------------------------
import { type MediaAsset } from '../../core/model';
import { formatTimecode } from '../../shared/time';
import type { EditorContext } from '../context';
import { clear, el } from '../dom';
import { placeAsset } from '../place-clip';

export function createMediaPanel(ctx: EditorContext): HTMLElement {
  const { store } = ctx;

  const fileInput = el('input', {
    type: 'file',
    accept: 'video/*,image/*,audio/*',
    multiple: true,
    style: 'display:none',
  }) as HTMLInputElement;

  const dropzone = el(
    'div',
    {
      class: 'zf-dropzone',
      role: 'button',
      tabindex: '0',
      'aria-label': 'Import media: click or press Enter, or drop video / images here',
      onclick: () => fileInput.click(),
      onkeydown: (e) => {
        const key = (e as KeyboardEvent).key;
        if (key === 'Enter' || key === ' ') {
          e.preventDefault();
          e.stopPropagation(); // don't also trigger the global Space=play shortcut
          fileInput.click();
        }
      },
    },
    'Click to import, or drop video / images here',
  );
  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    void importFiles((e as DragEvent).dataTransfer?.files ?? null);
  });
  fileInput.addEventListener('change', () => {
    void importFiles(fileInput.files);
    fileInput.value = ''; // allow re-importing the same file
  });

  const grid = el('div', { class: 'zf-media-grid' });

  const panel = el(
    'div',
    { class: 'zf-panel' },
    el('h2', {}, 'Media'),
    el('p', { class: 'zf-panel-hint' }, 'Your media stays on your device — nothing is uploaded.'),
    dropzone,
    fileInput,
    grid,
  );

  renderGrid();
  store.subscribe(renderGrid);

  async function importFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        ctx.setStatus(`Importing ${file.name}…`);
        const asset = await ctx.media.importFile(file);
        // Persist the bytes so a saved project can re-open this media later.
        try {
          await ctx.scratch.put(asset.id, file);
          asset.scratchKey = asset.id;
        } catch {
          /* scratch is best-effort; import still works in-memory */
        }
        store.addAsset(asset);
        ctx.setStatus(`Imported ${asset.name}.`);
      } catch (err) {
        ctx.setStatus(err instanceof Error ? err.message : 'Import failed.', 'error');
      }
    }
  }

  function renderGrid(): void {
    clear(grid);
    if (store.state.assets.length === 0) {
      grid.append(el('div', { class: 'zf-panel-hint' }, 'No media yet.'));
      return;
    }
    for (const asset of store.state.assets) grid.append(mediaItem(asset));
  }

  function mediaItem(asset: MediaAsset): HTMLElement {
    const item = el(
      'div',
      {
        class: 'zf-media-item',
        title: 'Click to add, or drag onto the timeline',
        draggable: 'true',
      },
      el('div', { class: 'zf-media-thumb' }, asset.kind === 'image' ? '🖼' : asset.kind === 'audio' ? '🎵' : '🎞'),
      el('div', { class: 'zf-media-name' }, asset.name),
      el('div', { class: 'zf-media-meta' }, metaText(asset)),
    );
    // Click → append to the timeline.
    item.addEventListener('click', () => placeAsset(ctx, asset));
    // Drag → drop onto a specific time/track (handled by the timeline).
    item.addEventListener('dragstart', (e) => {
      e.dataTransfer?.setData('application/x-zeroframe-asset', asset.id);
      e.dataTransfer?.setData('text/plain', asset.name);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'copy';
      item.classList.add('dragging');
    });
    item.addEventListener('dragend', () => item.classList.remove('dragging'));
    return item;
  }

  function metaText(asset: MediaAsset): string {
    if (asset.kind === 'audio') return `🔊 ${formatTimecode(asset.durationSec)}`;
    const size = `${asset.width}×${asset.height}`;
    return asset.kind === 'image' ? size : `${size} · ${formatTimecode(asset.durationSec)}`;
  }

  return panel;
}
